import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@kognitos/lattice';

import {
  getListAutomationsInfiniteQueryKey,
  getListAutomationsQueryKey,
  getListAutomationSummariesInfiniteQueryKey,
  getListAutomationSummariesQueryKey,
  useForkPublishedAutomation,
  useQueryAutomation,
} from '@/generated/hooks/grimoire/v1/api';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models';
import {
  getAutomationListThreadsQueryKey,
  useAutomationCreateThread,
  useAutomationSendMessage,
} from '@/generated/hooks/rain/v1/automation_scoped/thread_service';
import { RAIN_QUILL_AGENT_ID } from '@/modules/rain/agents/quill';
import { buildRainUserMessage } from '@/modules/rain/utils/message';
import { captureException } from '@/modules/sentry/helpers/capture';
import { errorHandler } from '@/shared/utils/error-handler';
import { getRoutePath } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

// Same convention as AutomationEditor's other trigger messages (e.g.
// MAKE_DETERMINISTIC_MESSAGE): the wrapper tag is a frontend display label
// only, the agent acts on the plain-language intent.
const UPGRADE_MANIFEST_MESSAGE =
  '<user_action type="upgrade_manifest">This automation was built before ' +
  'the step-by-step view existed. Re-validate and save it so the Steps ' +
  'view works.</user_action>';

export interface IUseUpgradeAutomationManifestOptions {
  /**
   * Called with the newly created thread id instead of navigating to the
   * draft editor — for a caller that's already looking at this
   * automation's own draft editor (AutomationEditor's draft-with-open-chat
   * case), where there's nothing to navigate to; it just needs to swap the
   * active thread in place (pass `useRainThread`'s
   * `setSelectedRainThreadId`). Omit this for callers with no open editor
   * (Details page, read-only Document view) to get the default
   * navigate-to-draft-editor behavior.
   */
  onThreadCreated?: (threadId: string) => void;
}

export interface IUseUpgradeAutomationManifestResult {
  /**
   * Forks a draft (only if this automation doesn't already have one — a
   * second fork would reset an in-progress draft back to the published
   * state), always opens a FRESH thread on it (never reuses whatever
   * thread is currently active — this prompt shouldn't land mid-way
   * through an unrelated conversation), sends the upgrade prompt, then
   * either hands the new thread id to `onThreadCreated` or navigates to
   * the draft editor.
   */
  upgrade: () => Promise<void>;
  /** True for the whole fork/create-thread/send-message sequence. */
  isUpgrading: boolean;
  /**
   * True once `upgrade()` has completed successfully. Callers that stay on
   * the same page (`onThreadCreated` given) use this to swap the banner's
   * button for a confirmation; it never resets — the banner itself
   * unmounts on its own once the agent's re-save lands a manifest.
   */
  hasRequested: boolean;
}

/**
 * Drives the manifest-upgrade banner everywhere it appears: the published
 * automation's Details page, its read-only Document view, and
 * AutomationEditor's own draft-with-open-chat case (via `onThreadCreated`).
 * A pre-existing draft is never re-forked (that resets it to the published
 * state); we still always open a brand-new thread and send the prompt
 * there rather than reusing whatever thread is currently active — the
 * freshly created thread becomes the draft editor's "latest" thread, so
 * it's what the user lands on (or is switched to in place).
 *
 * CALLERS MUST GATE ON `automation.kind === AUTOMATION_KIND_QUILL2` before
 * ever invoking `upgrade()`. This always creates a `quill2`-agent thread
 * and sends into it — for a legacy Quill v1 automation (which never gets a
 * `code_manifest` at all; it doesn't go through jarvis) that message lands
 * in a thread the automation's real (v1) chat never looks at, AND has been
 * observed to destroy the automation's existing v1 draft server-side. All
 * current call sites already gate on this — do not add a new one without
 * the same check.
 */
export function useUpgradeAutomationManifest(
  automationId: string,
  options: IUseUpgradeAutomationManifestOptions = {},
): IUseUpgradeAutomationManifestResult {
  const { onThreadCreated } = options;
  const { t } = useTranslation('automation');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data: draftData } = useQueryAutomation(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    { stage: V1AutomationStage.AUTOMATION_STAGE_DRAFT },
    {
      query: {
        retry: false,
        enabled: Boolean(
          automationId && currentOrganizationId && currentWorkspaceId,
        ),
      },
    },
  );
  const hasExistingDraft = Boolean(draftData?.data?.version);

  const { mutateAsync: forkPublishedAutomationAsync, isPending: isForking } =
    useForkPublishedAutomation();
  const { mutateAsync: createRainThreadAsync, isPending: isCreatingThread } =
    useAutomationCreateThread();
  const { mutateAsync: sendRainMessageAsync, isPending: isSendingMessage } =
    useAutomationSendMessage();
  const [hasRequested, setHasRequested] = useState(false);

  const upgrade = useCallback(async () => {
    try {
      if (!hasExistingDraft) {
        await forkPublishedAutomationAsync({
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          automation: automationId,
          data: {},
        });
        // Same invalidation set as AutomationOpenDraftDialog's fork —
        // without it the Drafts list serves stale cache until a hard
        // refresh (OC-1281).
        queryClient.invalidateQueries({
          queryKey: getListAutomationsQueryKey(
            currentOrganizationId,
            currentWorkspaceId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: getListAutomationSummariesQueryKey(
            currentOrganizationId,
            currentWorkspaceId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: getListAutomationsInfiniteQueryKey(
            currentOrganizationId,
            currentWorkspaceId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: getListAutomationSummariesInfiniteQueryKey(
            currentOrganizationId,
            currentWorkspaceId,
          ),
        });
      }

      const threadResponse = await createRainThreadAsync({
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        agentId: RAIN_QUILL_AGENT_ID,
        data: {
          variables: {
            organizationId: currentOrganizationId,
            workspaceId: currentWorkspaceId,
            automationId,
          },
        },
      });
      const threadId = threadResponse.data?.thread_id;
      if (!threadId) {
        throw new Error('Thread creation returned no thread id');
      }
      // Matters for the in-editor caller (onThreadCreated): its thread-list
      // query is already mounted, so without this the thread selector
      // wouldn't know the new thread exists until some unrelated refetch.
      // The outside-editor caller (navigate) doesn't need this — it always
      // remounts fresh and seeds `initialThreadId` directly, bypassing list
      // discovery entirely.
      queryClient.invalidateQueries({
        queryKey: getAutomationListThreadsQueryKey(
          currentOrganizationId,
          currentWorkspaceId,
          automationId,
          RAIN_QUILL_AGENT_ID,
        ),
      });

      await sendRainMessageAsync({
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        agentId: RAIN_QUILL_AGENT_ID,
        threadId,
        data: { message: buildRainUserMessage(UPGRADE_MANIFEST_MESSAGE) },
      });

      setHasRequested(true);
      if (onThreadCreated) {
        onThreadCreated(threadId);
      } else {
        navigate(
          getRoutePath('DRAFT_EDITOR', {
            orgId: currentOrganizationId,
            workspaceId: currentWorkspaceId,
            draftId: automationId,
          }),
          // Lets AutomationEditor bind to this exact thread on its first
          // render instead of discovering it as the "latest" thread — see
          // useRainThread's initialThreadId doc for why that race matters.
          { state: { pendingRainThreadId: threadId } },
        );
      }
    } catch (error) {
      errorHandler(error, () => {
        toast.error(t('automation.manifestSopView.error.upgradeManifest'));
        captureException(error as Error, {
          tags: {
            component: 'useUpgradeAutomationManifest',
            action: 'upgrade',
          },
        });
      });
    }
  }, [
    automationId,
    createRainThreadAsync,
    currentOrganizationId,
    currentWorkspaceId,
    forkPublishedAutomationAsync,
    hasExistingDraft,
    navigate,
    onThreadCreated,
    queryClient,
    sendRainMessageAsync,
    t,
  ]);

  return {
    upgrade,
    isUpgrading: isForking || isCreatingThread || isSendingMessage,
    hasRequested,
  };
}
